import { MapBase } from 'App/Map/MapContext/MapBase'
import { SelectedEquipmentCard } from 'App/ServiceCenter/SelectedEquipmentCard/SelectedEquipmentCard'
import {
  downloadOfflineContents,
  loadMapData,
  loadStartupData,
} from 'App/ServiceCenter/utils/loadStartupData'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TBlock } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { MapContext } from '../../Map/MapContext/MapContext'
import { MapActionIcons } from '../../Map/MapControls/MapActionIcons/MapActionIcons'
import { CurrentLocation } from '../../Map/_utils/CurrentLocation/CurrentLocation'
import { useBleManager } from '../BluetoothLowEnergy/BleManager'
import { OfflineContentDownloadBanner } from '../OfflineContentDownload/OfflineContentDownloadBanner'
import { ReloadMapButton } from '../ReloadMapButton/ReloadMapButton'
import { networkStore } from '../store/networkStore'
import { serviceCenterStore } from '../store/serviceCenterStore'
import { TNodeStatus } from '../types'
import { createErrorModal } from '../utils/createCommonModals'
import { ActiveEquipmentLayer } from './Layers/ActiveEquipment'
import { BlockLayer } from './Layers/BlockLayer'
import { PlannedEquipmentLayer } from './Layers/PlannedEquipment'
import { PropertiesOverviewLayer } from './Layers/PropertiesOverview'

export const DATA_REFRESH_DELAY = 1000 * 60 * 5 // 5 minutes

export const ServiceCenterMap = (props: {
  hasAppStartupData: boolean // TODO: Move...
}) => {
  const { map, setLoadingData, loadingData } = useContext(MapContext)
  const refreshInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  const zoomChangeListener = useRef<google.maps.MapsEventListener | undefined>(undefined)
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const properties = fieldAssetStore.useSelector((s) => s?.properties)
  const userPropertyIds = properties ? Object.keys(properties).map((id) => +id) : []
  const propertyOverviewZoomLevel = serviceCenterStore.useSelector((s) => s.propertyOverviewZoomLevel)
  const bleManager = useBleManager()
  const { connected } = networkStore.useSelector(networkStore.selectors.getConnectionStatus)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load initial static data
        await loadStartupData()
      } catch (error) {
        AboveAllModal.open(
          createErrorModal(translate.phrases.placeholder('Server Error'), (error as Error).message),
        )
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    bleManager.initialize()

    return function cleanup() {
      bleManager.finalize()
    }
  }, [])

  useEffect(() => {
    if (zoomChangeListener.current) zoomChangeListener.current.remove()

    zoomChangeListener.current = map?.addListener('zoom_changed', () => {
      const zoom = map.getZoom()

      // if the zoom level is less than the propertyOverviewZoomLevel, get back to property overview mode
      if (zoom && zoom <= propertyOverviewZoomLevel && selectedPropertyId) {
        serviceCenterStore.actions.selectProperty(null)
      }

      if (zoom && zoom >= 15) {
        if (selectedPropertyId) return

        const mapBounds = map?.getBounds()

        if (mapBounds) {
          const propertyId = serviceCenterStore.actions.getPropertyIdByMapBounds(mapBounds)

          serviceCenterStore.setState((s) => ({ ...s, propertyOverviewZoomLevel: 12 }))

          if (propertyId) serviceCenterStore.actions.selectProperty(propertyId)
        }
      }
    })

    return () => {
      if (zoomChangeListener.current) zoomChangeListener.current.remove()
    }
  }, [map, propertyOverviewZoomLevel, selectedPropertyId])

  useEffect(() => {
    const timerId = setTimeout(() => {
      // get static data (device/node help guides) only when the app is loaded for the first time
      // after first run keep downloading user properties data every 5 minutes
      // give 15 seconds delay to start first requests to wait pending requests finishes first after getting back online from offline
      connected && downloadOfflineContents(userPropertyIds)
    }, 100)

    // eslint-disable-next-line no-console
    console.log(
      `Next data refresh @ ${moment.tz().add(DATA_REFRESH_DELAY, 'milliseconds').local().format('HH:mm:ss')}`,
    )

    refreshInterval.current = setInterval(() => {
      connected && downloadOfflineContents(userPropertyIds)

      // eslint-disable-next-line no-console
      console.log(
        `Next data refresh @ ${moment
          .tz()
          .add(DATA_REFRESH_DELAY, 'milliseconds')
          .local()
          .format('HH:mm:ss')}`,
      )
    }, DATA_REFRESH_DELAY)

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current)

      if (timerId) clearTimeout(timerId)
    }
  }, [connected])

  useEffect(() => {
    setLoadingData(true)

    const fetchData = async () => {
      try {
        // Only fetch map data if there are selected properties
        if (selectedPropertyId && connected) await loadMapData([selectedPropertyId])

        setLoadingData(false)
      } catch (error) {
        AboveAllModal.open(
          createErrorModal(translate.phrases.placeholder('Server Error'), (error as Error).message),
        )
      }
    }

    fetchData()

    window.Intercom('hide')

    return function cleanup() {
      serviceCenterStore.actions.unselectEquipment()
    }
  }, [selectedPropertyId])

  const mapEquipmentStatuses = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getMapEquipmentStatuses,
  )

  const plannedNodes = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getPlannedNodesForSelectedProperties(s, selectedPropertyId),
  )

  const activeNodes = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getActiveNodesForSelectedProperties(s, selectedPropertyId),
  )

  const blocks = useMemo(() => {
    let propertiesBlocks: TBlock[] = []

    if (selectedPropertyId) {
      const property = properties?.[selectedPropertyId]

      if (!property?.blocks) return []

      propertiesBlocks = propertiesBlocks.concat(Object.values(property.blocks))
    }

    return propertiesBlocks
  }, [selectedPropertyId, properties])

  const showPlannedNodes = !mapEquipmentStatuses.length || mapEquipmentStatuses.includes(TNodeStatus.PLANNED)

  return (
    <MapBase hasAppStartupData={props.hasAppStartupData} loadingData={loadingData} onInit={undefined}>
      {!selectedPropertyId && <PropertiesOverviewLayer />}
      {selectedPropertyId && map ? (
        <>
          <BlockLayer blocks={blocks} />
          {showPlannedNodes && <PlannedEquipmentLayer plannedNodes={plannedNodes} />}
          <ActiveEquipmentLayer activeNodes={activeNodes} />
          <SelectedEquipmentCard />

          <MapActionIcons>
            <CurrentLocation enableHighAccuracy={false} />
            <ReloadMapButton />
          </MapActionIcons>
        </>
      ) : null}
      <OfflineContentDownloadBanner />
    </MapBase>
  )
}
